import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clientService from '../services/clientService';
import clientAccountService from '../services/clientAccountService';
import { ClienteResponseDTO } from '../types/client';
import { ClientAccountSummary, ClientAccountPaymentDTO, ClientAccountPaymentRequest } from '../types/clientAccount';
import alertService from '../services/alertService';
import { CreditCard, ArrowLeft, ListOrdered, X, ShoppingCart } from 'lucide-react';
import { paymentTypeService } from '../services/paymentTypeService';
import { PaymentType } from '../types/payment';
import { authService } from '../services/authService';
import vendaService from '../services/vendaService';
import { formatFromRaw, getNextRawOnKey, parseRawToNumber, rawFromNumber } from '../utils/decimalMask';


const currency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const ClientAccountInfoPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClienteResponseDTO | null>(null);
  const [summary, setSummary] = useState<ClientAccountSummary | null>(null);
  const [payments, setPayments] = useState<ClientAccountPaymentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<PaymentType[]>([]);
  const [sales, setSales] = useState<any[]>([]);
  const [salesPage, setSalesPage] = useState(0);
  const [salesSize, setSalesSize] = useState(10);
  const [salesTipoPagamentoId, setSalesTipoPagamentoId] = useState<string>('');
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<any | null>(null);
  const initRef = useRef<{ loadedAll: boolean; loadedSales: boolean }>({ loadedAll: false, loadedSales: false });

  //const [valor, setValor] = useState<string>('');
  const [tipoPagamentoId, setTipoPagamentoId] = useState<string>('');
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [valorRawMap, setValorRawMap] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'pagamentos' | 'vendas'>('pagamentos');

  const VALOR_DECIMALS = 2;
  const [valorRaw, setValorRaw] = useState<string>(() => rawFromNumber(0, VALOR_DECIMALS));
  const valor = parseRawToNumber(valorRaw, VALOR_DECIMALS);
  
  const STATUS_VENDAS_BADGE: Record<string, string> = {
    PENDENTE: 'bg-yellow-100 text-yellow-700',
    FINALIZADA: 'bg-green-100 text-green-700',
    CANCELADA: 'bg-red-100 text-red-700',
    EM_PROCESSAMENTO: 'bg-blue-100 text-blue-700',
    ENTREGUE: 'bg-emerald-100 text-emerald-700',
    PARCIALMENTE_ENTREGUE: 'bg-orange-100 text-orange-700',
  };

  useEffect(() => {
    if (registerModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [registerModalOpen]);

  const saldoFmt = useMemo(() => currency(Number(summary?.saldoDevedor || 0)), [summary]);

  const loadAll = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [cRes, sRes, pRes, tRes] = await Promise.all([
        clientService.getById(String(id)),
        clientAccountService.getAccount(String(id)),
        clientAccountService.listPayments(String(id), { page: 0, size: 50 }),
        paymentTypeService.list(),
      ]);
      setClient(cRes.data || null);
      setSummary(sRes.data || null);
      setPayments((pRes.data as any)?.content || (pRes.data as any) || []);
      const ts = Array.isArray(tRes.data) ? tRes.data : ((tRes.data as any)?.content || []);
      setTypes(ts.filter(Boolean));
    } catch (e: any) {
      alertService.error('Erro ao carregar conta do cliente', e?.message || '');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!initRef.current.loadedAll) {
      initRef.current.loadedAll = true;
      loadAll();
      return;
    }
    loadAll();
  }, [id]);

  const loadSales = async () => {
    if (!id) return;
    try {
      const r = await vendaService.listarPorCliente(String(id), { page: salesPage, size: salesSize, tipoPagamentoId: salesTipoPagamentoId || undefined });
      const payload: any = r?.data;
      const arr = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.content)
          ? payload.content
          : Array.isArray(payload)
            ? payload
            : [];
      setSales(arr || []);
    } catch (e: any) {
      alertService.error('Erro ao listar vendas do cliente', e?.message || '');
    }
  };

  useEffect(() => {
    if (!initRef.current.loadedSales) {
      initRef.current.loadedSales = true;
      loadSales();
      return;
    }
    loadSales();
  }, [id, salesPage, salesSize, salesTipoPagamentoId]);

  const openItemsModal = async (sale: any) => {
    try {
      if (!sale?.itens || sale.itens.length === 0) {
        const r = await vendaService.obter(String(sale.id));
        const payload: any = r.data;
        const venda = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload;
        setSelectedSale(venda);
      } else {
        setSelectedSale(sale);
      }
      setItemsModalOpen(true);
    } catch (e: any) {
      alertService.error('Falha ao carregar itens da venda', e?.message || '');
    }
  };

  const onRegistrarPagamento = async () => {
    if (!id) return;
    const v = parseFloat(String(valor).replace(',', '.'));
    if (isNaN(v) || v <= 0) { alertService.error('Informe um valor válido'); return; }
    if (!tipoPagamentoId) { alertService.error('Selecione a forma de pagamento'); return; }
    const usuarioId = String(authService.getLoginData()?.usuarioId || authService.getUser()?.id || '');
    const payload: ClientAccountPaymentRequest = { valor: v, usuarioId, tipoPagamentoId, numeroDocumento: numeroDocumento || undefined, observacoes: observacoes || undefined };
    try {
      alertService.loading('Registrando pagamento...');
      await clientAccountService.registerPayment(String(id), payload);
      alertService.close();
      alertService.success('Pagamento registrado');
      //setValor(''); setNumeroDocumento(''); setObservacoes(''); setTipoPagamentoId('');
      await loadAll();
    } catch (e: any) {
      alertService.close();
      alertService.error('Falha ao registrar pagamento', e?.message || '');
    }
  };

  const itemsModalItemVenda = (itemsModalOpen && selectedSale) ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setItemsModalOpen(false)}></div>
      <div role="dialog" aria-modal="true" className="relative bg-white rounded border shadow-lg w-full max-w-xl sm:max-w-2xl p-0 flex flex-col max-h-[90vh] min-h-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Itens da venda {selectedSale?.id}</h3>
          <button onClick={() => setItemsModalOpen(false)} className="text-gray-600 hover:text-gray-800 p-1 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left text-sm font-medium text-gray-700 px-3 py-2">Produto</th>
                <th className="text-right text-sm font-medium text-gray-700 px-3 py-2">Qtde</th>
                <th className="text-right text-sm font-medium text-gray-700 px-3 py-2">Preço</th>
                <th className="text-right text-sm font-medium text-gray-700 px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {(selectedSale?.itens || []).map((it: any, idx: number) => (
                <tr key={`${selectedSale.id}-${idx}`} className="border-t">
                  <td className="px-3 py-2 text-gray-700">{it.produtoNome || it.produtoId}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{it.quantidade}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{currency(Number(it.precoUnitario || 0))}</td>
                  <td className="px-3 py-2 text-right text-gray-900 font-medium">{currency(Number(it.valorTotal || ((it.quantidade || 0) * (it.precoUnitario || 0))))}</td>
                </tr>
              ))}
              {(!selectedSale?.itens || selectedSale.itens.length === 0) && (
                <tr><td className="px-3 py-6 text-center text-gray-500" colSpan={4}>Sem itens</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex justify-end">
          <button onClick={() => setItemsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-white bg-red-600 hover:bg-red-700">Fechar</button>
        </div>
      </div>
    </div>
  ) : null;

  const registerModal = registerModalOpen ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setRegisterModalOpen(false)}></div>
      <div role="dialog" aria-modal="true" className="relative bg-white rounded border shadow-lg w-full max-w-xl sm:max-w-2xl p-0 flex flex-col max-h-[90vh] min-h-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Registrar pagamento</h3>
            <button onClick={() => setRegisterModalOpen(false)} className="text-gray-600 hover:text-gray-800 p-1 rounded">
              <X className="h-5 w-5" />
            </button>
        </div>
        <form id="registerPaymentForm" onSubmit={async (e) => { e.preventDefault(); await onRegistrarPagamento(); setRegisterModalOpen(false); }} className="flex-1 min-h-0 overflow-y-auto px-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="md:col-span-2 flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
              <input
                type="tel"
                step="0.01"
                min="0"
                value={formatFromRaw(valorRaw, VALOR_DECIMALS)}
                          onKeyDown={(e) => {
                            const { handled, nextRaw } = getNextRawOnKey(e.key, valorRaw);
                            if (handled) {
                              e.preventDefault();
                              setValorRaw(nextRaw);
                            }
                          }}
                onChange={() => {}}
                className="w-full px-4 py-3 border-2 border-yellow-400 rounded-lg bg-yellow-50 text-2xl font-bold tracking-wide focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                placeholder={formatFromRaw(rawFromNumber(0, VALOR_DECIMALS), VALOR_DECIMALS)}
              />
            </div>
            <div className="md:col-span-2 flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento *</label>
              <select
                value={tipoPagamentoId}
                onChange={(e) => setTipoPagamentoId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">Selecione</option>
                {types.map(t => (<option key={t.id} value={t.id}>{t.nome}</option>))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">Número do documento</label>
              <input
                value={numeroDocumento}
                onChange={(e) => setNumeroDocumento(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2 flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        </form>
        <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex justify-end gap-3">
          <button type="button" onClick={() => setRegisterModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
          <button type="submit" form="registerPaymentForm" disabled={loading} className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50">
            Registrar
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/clientes/contas')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <CreditCard className="h-6 w-6 text-red-600" />
          <h1 className="text-xl font-bold text-gray-900">Conta do Cliente</h1>
        </div>
        <div className="flex items-center gap-2">
          {(summary  && summary?.saldoDevedor <= 0) ? <></> : (
          <button onClick={() => setRegisterModalOpen(true)} className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded inline-flex items-center gap-2">Registrar pagamento</button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Cliente</div>
              <div className="text-lg font-semibold text-gray-900">{client?.nome || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Saldo Devedor</div>
              <div className="text-4xl font-bold text-red-900">{saldoFmt}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-4 pt-4">
            <div role="tablist" aria-label="Dados da conta" className="flex gap-4">
              <button
                role="tab"
                aria-selected={activeTab === 'pagamentos'}
                onClick={() => setActiveTab('pagamentos')}
                className={`${activeTab === 'pagamentos' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-600 hover:text-gray-800'} flex flex-row px-2 pb-2 font-medium`}
              >
                <CreditCard /> &nbsp; Pagamentos
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'vendas'}
                onClick={() => setActiveTab('vendas')}
                className={`${activeTab === 'vendas' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-600 hover:text-gray-800'} flex flex-row px-2 pb-2 font-medium`}
              >
                <ShoppingCart /> &nbsp; Vendas
              </button>
            </div>
          </div>

          {activeTab === 'pagamentos' && (
            <div>
              <div className="p-4 border-t">
                <div className="text-lg font-semibold text-gray-900">Pagamentos efetuados</div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Data</th>
                      <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Forma</th>
                      <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Documento</th>
                      <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Observações</th>
                      <th className="text-right text-sm font-medium text-gray-700 px-4 py-2">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 && (
                      <tr>
                        <td className="px-4 py-6 text-center text-gray-500" colSpan={5}>Nenhum pagamento registrado</td>
                      </tr>
                    )}
                    {payments.map((p) => (
                      <tr key={p.id} className="border-t">
                        <td className="px-4 py-2 text-gray-700">{p.dataCriacao ? new Date(p.dataCriacao).toLocaleString('pt-BR') : '-'}</td>
                        <td className="px-4 py-2 text-gray-700">{p.tipoPagamentoNome || '-'}</td>
                        <td className="px-4 py-2 text-gray-700">{p.numeroDocumento || '-'}</td>
                        <td className="px-4 py-2 text-gray-700">{p.observacoes || '-'}</td>
                        <td className="px-4 py-2 text-gray-900 text-right font-medium">{currency(p.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'vendas' && (
            <div>
              <div className="p-4 border-t flex items-center justify-between">
                <div className="text-lg font-semibold text-gray-900">Vendas do cliente</div>
                <div className="flex items-center gap-2">
                  <select value={salesTipoPagamentoId} onChange={(e) => { setSalesTipoPagamentoId(e.target.value); setSalesPage(0); }} className="px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="">Todas as formas</option>
                    {types.map(t => (<option key={t.id} value={t.id}>{t.nome}</option>))}
                  </select>
                  <button onClick={() => loadSales()} className="px-3 py-2 border rounded-lg">Filtrar</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">ID</th>
                      <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Data</th>
                      <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Status</th>
                      <th className="text-right text-sm font-medium text-gray-700 px-4 py-2">Valor Total</th>
                      <th className="text-right text-sm font-medium text-gray-700 px-4 py-2">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.length === 0 && (
                      <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={5}>Nenhuma venda encontrada</td></tr>
                    )}
                    {sales.map((v: any) => (
                      <tr key={v.id} className="border-t">
                        <td className="px-4 py-2 text-gray-700">{v.id}</td>
                        <td className="px-4 py-2 text-gray-700">{v.dataCriacao ? new Date(v.dataCriacao).toLocaleString('pt-BR') : (v.dataVenda ? new Date(v.dataVenda).toLocaleString('pt-BR') : '-')}</td>
                        <td className="px-4 py-2 text-gray-700"> <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_VENDAS_BADGE[v.status] || 'bg-gray-100 text-gray-700'}`}>{v.status}</span></td>
                        <td className="px-4 py-2 text-gray-900 text-right font-medium">{currency(Number(v.valorTotal || 0))}</td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => openItemsModal(v)} className="inline-flex items-center gap-1 px-3 py-1 border rounded">
                            <ListOrdered className="h-4 w-4" /> Itens
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {itemsModalItemVenda}

      {registerModal}
    </div>
  );
};

export default ClientAccountInfoPage;